// The login screen's colours (issue 1140), worked out from what the tenant chose:
// a text/primary colour, a box colour with an opacity, and whether the box is
// light or dark. The screen does not follow the app's light/dark theme — the
// box's tone decides — so everything on it is set here as token overrides on
// its subtree: the inks, the fields, the buttons, the links, the canvas.
//
// One function, two callers: LoginView (the real screen) and LoginBrandingView's
// preview, so the miniature cannot drift from what visitors see.

export type LoginTone = 'light' | 'dark';

export const DEFAULT_BOX_COLOR = '#ffffff';
export const DEFAULT_BOX_OPACITY = 60;
// Below this, the text on the box answers to whatever photo is behind it.
export const MIN_BOX_OPACITY = 30;

export interface LoginSurfaceInput {
  primaryColor?: string | null;
  boxColor?: string | null;
  boxOpacity?: number | null;
  boxTone?: LoginTone | null;
}

type Rgb = { r: number; g: number; b: number };

const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const FUNC = /^(rgba?|hsla?)\(\s*([^)]*)\)$/i;

/** A colour in any grammar user-api stores (hex, rgb(), hsl()), or null. */
export function parseColor(value: string | null | undefined): Rgb | null {
  const raw = (value ?? '').trim();
  const hex = HEX.exec(raw);
  if (hex) {
    let h = hex[1];
    if (h.length <= 4) h = [...h].map((c) => c + c).join('');
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
  }
  const fn = FUNC.exec(raw);
  if (!fn) return null;
  const parts = fn[2].split(/[\s,/]+/).filter(Boolean).map((p) => ({ n: parseFloat(p), pct: p.endsWith('%') }));
  if (parts.length < 3 || parts.slice(0, 3).some((p) => Number.isNaN(p.n))) return null;
  if (fn[1].toLowerCase().startsWith('rgb')) {
    const ch = (p: { n: number; pct: boolean }) => Math.max(0, Math.min(255, p.pct ? (p.n / 100) * 255 : p.n));
    return { r: ch(parts[0]), g: ch(parts[1]), b: ch(parts[2]) };
  }
  const h = (((parts[0].n % 360) + 360) % 360) / 360;
  const s = Math.max(0, Math.min(1, parts[1].n / 100));
  const l = Math.max(0, Math.min(1, parts[2].n / 100));
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    const x = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return { r: hue(h + 1 / 3) * 255, g: hue(h) * 255, b: hue(h - 1 / 3) * 255 };
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance({ r, g, b }: Rgb): number {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function contrast(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The tone a box colour asks for: dark text where dark text reads better on it,
 * light text otherwise (luminance 0.179 is where black and white contrast equally).
 * The form flips the switch to this when the box colour changes; the admin may
 * flip it back.
 */
export function suggestedTone(boxColor: string | null | undefined): LoginTone {
  const box = parseColor(boxColor || DEFAULT_BOX_COLOR);
  return !box || luminance(box) > 0.179 ? 'light' : 'dark';
}

export interface LoginSurface {
  tone: LoginTone;
  /** Token overrides for the screen's (or the preview's) root element. */
  style: Record<string, string>;
}

export function loginSurface(input: LoginSurfaceInput): LoginSurface {
  const boxColor = parseColor(input.boxColor) ? String(input.boxColor).trim() : DEFAULT_BOX_COLOR;
  const box = parseColor(boxColor)!;
  const opacity = Number.isFinite(input.boxOpacity) ? Math.max(0, Math.min(100, Number(input.boxOpacity))) : DEFAULT_BOX_OPACITY;
  const tone: LoginTone = input.boxTone === 'light' || input.boxTone === 'dark' ? input.boxTone : suggestedTone(boxColor);

  const ink = `var(--login-ink-${tone})`;
  const inkSoft = `var(--login-ink-soft-${tone})`;
  // The fields stay opaque whatever the slider says: a field over a photo has to
  // read as a field. They are the box's colour, pushed toward paper on a light
  // box and toward night on a dark one, so they belong to the box they sit on.
  const field = tone === 'light'
    ? `color-mix(in srgb, ${boxColor} 15%, var(--login-paper))`
    : `color-mix(in srgb, ${boxColor} 40%, var(--login-night))`;
  const line = `color-mix(in srgb, ${ink} 22%, transparent)`;

  // The tenant's colour titles the box, colours its links and fills the sign-in
  // button — but text in it must still read on the box: under 3:1 (the large-text
  // floor) the title and links fall back to the box's ink. The button keeps the
  // colour and takes whichever label reads on it.
  const primary = parseColor(input.primaryColor) ? String(input.primaryColor).trim() : null;
  const primaryRgb = primary ? parseColor(primary) : null;
  const readable = !!primaryRgb && contrast(primaryRgb, box) >= 3;
  const accentText = readable ? primary! : ink;
  const buttonBg = primary ?? ink;
  const buttonLabel = primaryRgb
    ? luminance(primaryRgb) > 0.179 ? 'var(--login-ink-light)' : 'var(--login-paper)'
    : tone === 'light' ? 'var(--login-paper)' : 'var(--login-night)';

  return {
    tone,
    style: {
      '--login-box': `color-mix(in srgb, ${boxColor} ${opacity}%, transparent)`,
      '--surface-canvas': `var(--login-canvas-${tone})`,
      '--text-title': accentText,
      '--text-heading': ink,
      '--text-body': ink,
      '--text-secondary': inkSoft,
      '--text-tertiary': inkSoft,
      '--field-bg': field,
      '--field-border': line,
      '--border-default': line,
      '--border-strong': line,
      '--panel-edge': line,
      '--surface-card': field,
      // Hover: a step of ink into the field, opaque like the field itself.
      '--surface-wash': `color-mix(in srgb, ${ink} 8%, ${field})`,
      '--btn-primary-bg': buttonBg,
      '--btn-primary-fg': buttonLabel,
      '--text-inverse': buttonLabel,
      '--accent': accentText,
      // The primary button's hover (Button reads --accent-hover): its own
      // colour, a step darker.
      '--accent-hover': `color-mix(in srgb, ${buttonBg} 85%, var(--login-night))`,
      '--accent-text': accentText,
      '--accent-ring': `color-mix(in srgb, ${buttonBg} 35%, transparent)`,
      '--focus-ring': ink,
    },
  };
}
